import { Container } from 'pixi.js';
import { RealSpineAdapter } from '../animation/RealSpineAdapter.js';
import { createSpineInstance, createWinFrameSpineInstance } from '../rendering/spineLoader.js';
import { gameConfig } from '../config/gameConfig.js';

export class Symbol {
  /**
   * @param {{
   *   spineAssets: Record<number, any>,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   * }} opts
   */
  constructor(opts) {
    this.spineAssets = opts.spineAssets;
    this.animationController = opts.animationController;

    this.root = new Container();
    this._symbolId = 0;
    this._spine = null;
    this._adapter = null;
    /** @type {import('@esotericsoftware/spine-pixi-v8').Spine | null} */
    this._winFrameSpine = null;
    this._isSpinning = false;
    this._isActive = false;
    this._renderedSymbolId = null;
    /** True after {@link #playWin} until dim / normal / reset / spin. */
    this._winClipPlaying = false;
    /** Payline-cycle grid state — avoids stop→idle flash when re-applying the same presentation. */
    this._gridPresentation = 'normal';
  }

  /**
   * @param {number} symbolId
   */
  _createSpineForSymbol(symbolId) {
    if (!Number.isFinite(symbolId) || symbolId <= 0) {
      this._renderedSymbolId = symbolId;
      this._adapter = null;
      return;
    }

    if (this._spine) {
      this.root.removeChild(this._spine);
      this._spine.destroy();
      this._spine = null;
    }

    const spine = createSpineInstance(symbolId, this.spineAssets);
    if (spine) {
      this._spine = spine;
      this._renderedSymbolId = symbolId;
      this.root.addChild(this._spine);
      this._sendWinFrameToBack();

      this._adapter = new RealSpineAdapter(this.root, spine, {
        animationController: this.animationController,
      });
      // Always start idle; _isSpinning will be re-applied by setActive if needed.
      this._isSpinning = false;
      this._adapter.setIdle();
    } else {
      console.warn(`Failed to create spine for symbol ${symbolId}`);
    }
  }

  _sendWinFrameToBack() {
    if (this._winFrameSpine?.parent === this.root) {
      this.root.addChildAt(this._winFrameSpine, 0);
    }
  }

  _ensureWinFrameSpine() {
    if (this._winFrameSpine) return;
    const spine = createWinFrameSpineInstance(this.spineAssets);
    if (!spine) return;
    this._winFrameSpine = spine;
    this.root.addChild(spine);
  }

  _hideWinFrameSpine() {
    if (!this._winFrameSpine) return;
    this._winFrameSpine.visible = false;
    try {
      this._winFrameSpine.state?.clearTracks();
    } catch {
      /* ignore */
    }
  }

  /**
   * @returns {string | null}
   */
  _resolveWinClipName() {
    const data = this._winFrameSpine?.skeleton?.data ?? this._spine?.skeleton?.data;
    if (!data) return null;
    const a = gameConfig.animations;
    if (a?.win && data.findAnimation(a.win)) return a.win;
    return data.findAnimation('win') ? 'win' : null;
  }

  /**
   * @param {import('@esotericsoftware/spine-pixi-v8').Spine} spine
   * @param {string} clipName
   * @param {boolean} [loop]
   */
  _setWinClipOnSpine(spine, clipName, loop = true) {
    if (!spine?.state) return;
    try {
      const entry = spine.state.setAnimation(0, clipName, loop);
      if (entry) entry.mixDuration = 0;
    } catch {
      /* ignore */
    }
  }

  _stopWinClipPlayback() {
    this._winClipPlaying = false;
    this._hideWinFrameSpine();
    if (this._adapter && typeof this._adapter.stop === 'function') {
      this._adapter.stop();
    }
    if (this._spine?.skeleton) {
      try {
        this._spine.state?.clearTracks();
        this._spine.skeleton.setToSetupPose();
      } catch {
        /* ignore */
      }
    }
  }

  /** Stop win frame/clips and full opacity — safe on every strip cell (cheap). */
  clearWinArtifacts() {
    this._stopWinClipPlayback();
    this._gridPresentation = 'normal';
    this.root.alpha = 1;
  }

  /**
   * Start looping symbol + frame `win` clips until dim / spin / reset.
   */
  playWin() {
    if (!this._isActive || !this._adapter || this._winClipPlaying) return;

    const clipName = this._resolveWinClipName();
    if (!clipName) return;

    this._winClipPlaying = true;

    this._ensureWinFrameSpine();
    if (this._winFrameSpine) {
      this._winFrameSpine.visible = true;
      this._sendWinFrameToBack();
      this._setWinClipOnSpine(this._winFrameSpine, clipName, true);
    }

    this._adapter.setWin();
  }

  /**
   * Play one full `win` from frame 0 (no loop). Used when cycling paylines so each line restarts cleanly.
   */
  playWinOnce() {
    if (!this._isActive || !this._adapter) return;

    const clipName = this._resolveWinClipName();
    if (!clipName) return;

    this._stopWinClipPlayback();

    this._winClipPlaying = true;

    this._ensureWinFrameSpine();
    if (this._winFrameSpine) {
      this._winFrameSpine.visible = true;
      this._sendWinFrameToBack();
      this._setWinClipOnSpine(this._winFrameSpine, clipName, false);
    }

    if (typeof this._adapter.setWinOnce === 'function') {
      this._adapter.setWinOnce();
    } else {
      this._adapter.setWin();
    }
  }

  getAdapter() {
    return this._adapter;
  }

  /** @returns {number} Spine `win` clip length in seconds (0 if unavailable). */
  getWinClipDurationSec() {
    let max = 0;
    const a = this._adapter;
    if (a && typeof a.getClipDurationSec === 'function') {
      max = Math.max(max, a.getClipDurationSec(gameConfig.animations.win));
    }
    const clipName = this._resolveWinClipName();
    const frameData = this._winFrameSpine?.skeleton?.data ?? this._spine?.skeleton?.data;
    if (clipName && frameData) {
      const anim = frameData.findAnimation(clipName);
      if (anim?.duration > 0) max = Math.max(max, anim.duration);
    }
    return max;
  }

  /** @param {number} symbolId */
  setSymbolId(symbolId) {
    this._symbolId = symbolId;
    if (!this._isActive) return;
    if (this._renderedSymbolId === symbolId) return;
    this._createSpineForSymbol(symbolId);
  }

  getSymbolId() {
    return this._symbolId;
  }

  /**
   * @param {boolean} isSpinning
   */
  setSpinning(isSpinning) {
    if (!isSpinning) {
      this.clearSpinPresentation();
      return;
    }
    if (!this._isActive) return;   // never flag or animate an invisible cell
    if (this._isSpinning) return;
    this._isSpinning = true;
    this._winClipPlaying = false;
    this._hideWinFrameSpine();
    if (this._adapter) {
      if (typeof this._adapter.setSpinning === 'function') {
        this._adapter.setSpinning();
      } else {
        this._adapter.setBlur?.();
      }
    }
  }

  /** Drop reel blur/spin clip on this cell — always clears the flag AND the adapter, even when inactive. */
  clearSpinPresentation() {
    this._isSpinning = false;
    this._adapter?.setIdle?.();
  }

  /**
   * Grid presentation only — does not start Spine `win` clips (use {@link #playWin}).
   * Non-winners on a payline step use the `noWin` clip (design intent). Cleared in
   * {@link #clearWinArtifacts} / {@link #prepareForNewRound} before the next spin.
   * @param {'normal' | 'dim' | 'highlight'} state
   */
  setWinPresentation(state) {
    if (!this._isActive || !this._adapter) return;
    this.root.alpha = 1;

    if (state === 'dim') {
      if (this._gridPresentation === 'dim') return;
      this._stopWinClipPlayback();
      this._gridPresentation = 'dim';
      this._adapter.setNoWin();
      return;
    }

    if (state === 'highlight') {
      this._gridPresentation = 'highlight';
      return;
    }

    if (this._gridPresentation === 'normal') return;
    this._stopWinClipPlayback();
    this._gridPresentation = 'normal';
    this._adapter.setIdle();
  }

  setHighlight(on) {
    this.setWinPresentation(on ? 'highlight' : 'normal');
  }

  /**
   * Hard reset before a new spin: idle adapters, no win/spin flags, neutral transform.
   * @param {boolean} [forceAdapter] When true, reset Spine even if the cell is off-screen inactive.
   */
  prepareForNewRound(forceAdapter = false) {
    this.clearWinArtifacts();
    this._isSpinning = false;
    this.root.scale.set(1, 1);
    this.root.rotation = 0;
    if (!forceAdapter && !this._isActive) return;
    if (!this._adapter) return;
    this._adapter.reset();
    this._adapter.setIdle();
  }

  resetVisual() {
    this.prepareForNewRound();
  }

  setActive(isActive) {
    if (this._isActive === isActive) return;
    this._isActive = isActive;
    this.root.visible = isActive;
    this.root.renderable = isActive;
    if (!isActive) {
      this._isSpinning = false;
      this._winClipPlaying = false;
      this._hideWinFrameSpine();
      this._adapter?.stop?.();
      return;
    }
    if (this._renderedSymbolId !== this._symbolId) {
      this._createSpineForSymbol(this._symbolId);
    }
    // _createSpineForSymbol resets _isSpinning to false; never re-apply blur on activation.
    this._adapter?.setIdle?.();
  }

  isSpinning() {
    return this._isSpinning;
  }

  destroy() {
    if (this.root.destroyed) return;
    try {
      this._adapter?.stop?.();
    } catch {
      /* ignore */
    }
    this._adapter = null;
    this._spine = null;
    this._winFrameSpine = null;
    this.root.destroy({ children: true });
  }
}
